import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { ReactComponent as BoldIcon } from "../../assets/icons/svg/Bold_icon.svg";
import { ReactComponent as ItalicIcon } from "../../assets/icons/svg/Italic_icon.svg";
import { ReactComponent as UnderlineIcon } from "../../assets/icons/svg/Underline_icon.svg";
import { ReactComponent as DotLineIcon } from "../../assets/icons/svg/Dot_line_icon.svg";
import { ReactComponent as NumberLineIcon } from "../../assets/icons/svg/Number_line_icon.svg";
import { ReactComponent as LinkIcon } from "../../assets/icons/svg/Link_icon.svg";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";


interface TextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  onFocus?: () => void;  
  onBlur?: () => void;   
}
const MenuBar = ({ editor }: { editor: any }) => {
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);

  if (!editor) return null;

  const buttonClass = (active: boolean, disabled: boolean) =>
    `px-1 py-1 rounded text-sm font-medium transition-colors ${
      active
        ? "bg-gray-200 text-gray-900"
        : "bg-transparent text-gray-700 hover:bg-gray-100"
    } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`;

  const isDisabled = !editor.isEditable;

  const handleBulletList = (e: React.MouseEvent) => {
    e.preventDefault();
    editor.chain().focus().toggleBulletList().run();
    setTimeout(() => forceUpdate(), 0);
  };

  const handleOrderedList = (e: React.MouseEvent) => {
    e.preventDefault();
    editor.chain().focus().toggleOrderedList().run();
    setTimeout(() => forceUpdate(), 0);
  };

  const handleBold = (e: React.MouseEvent) => {
    e.preventDefault();
    editor.chain().focus().toggleBold().run();
    setTimeout(() => forceUpdate(), 0);
  };

  const handleItalic = (e: React.MouseEvent) => {
    e.preventDefault();
    editor.chain().focus().toggleItalic().run();
    setTimeout(() => forceUpdate(), 0);
  };

  const handleUnderline = (e: React.MouseEvent) => {
    e.preventDefault();
    editor.chain().focus().toggleUnderline().run();
    setTimeout(() => forceUpdate(), 0);
  };

  const handleAddLink = (e: React.MouseEvent) => {
    e.preventDefault();
    const { from, to } = editor.state.selection;

    // Check if there's selected text
    if (from === to) {
      alert("Please select some text first to add a link");
      return;
    }

    // Get the selected text
    const selectedText = editor.state.doc.textBetween(from, to, " ");

    // Check if already a link - if yes, remove it
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
      // Deselect after removing link
      editor.commands.setTextSelection({ from: to, to: to });
      setTimeout(() => forceUpdate(), 0);
      return;
    }

    // Convert selected text directly to a link
    // Use the selected text as the URL (you can modify this logic as needed)
    let url = selectedText.trim();

    // Add https:// if no protocol is specified
    if (!/^https?:\/\//i.test(url)) {
      url = "https://" + url;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();

    // Move cursor to the end and deselect
    editor.commands.setTextSelection({ from: to, to: to });

    // Unset link mark for next typing so it doesn't continue as a link
    editor.commands.unsetMark("link");

    setTimeout(() => forceUpdate(), 0);
  };

  return (
    <div className="inline-flex items-center gap-[2px] border-[0.5px] border-[#CBCED9] bg-[#FFFFFF99] rounded-lg py-1 px-1 shadow-md">
      <button
        type="button"
        onMouseDown={handleBold}
        disabled={isDisabled}
        className={buttonClass(editor.isActive("bold"), isDisabled)}
        title="Bold (Ctrl+B)"
      >
        <BoldIcon className="w-4 h-4 3xl:w-5 3xl:h-5" />
      </button>

      <button
        type="button"
        onMouseDown={handleItalic}
        disabled={isDisabled}
        className={buttonClass(editor.isActive("italic"), isDisabled)}
        title="Italic (Ctrl+I)"
      >
        <ItalicIcon className="w-4 h-4 3xl:w-5 3xl:h-5" />
      </button>

      <button
        type="button"
        onMouseDown={handleUnderline}
        disabled={isDisabled}
        className={buttonClass(editor.isActive("underline"), isDisabled)}
        title="Underline (Ctrl+U)"
      >
        <UnderlineIcon className="w-4 h-4 3xl:w-5 3xl:h-5" />
      </button>

      <div className="w-px h-4 bg-gray-300 mx-2" />

      <button
        type="button"
        onMouseDown={handleBulletList}
        disabled={isDisabled}
        className={buttonClass(editor.isActive("bulletList"), isDisabled)}
        title="Bullet List"
      >
        <DotLineIcon className="w-4 h-4 3xl:w-5 3xl:h-5" />
      </button>

      <button
        type="button"
        onMouseDown={handleOrderedList}
        disabled={isDisabled}
        className={buttonClass(editor.isActive("orderedList"), isDisabled)}
        title="Numbered List"
      >
        <NumberLineIcon className="w-4 h-4 3xl:w-5 3xl:h-5" />
      </button>
      
      <div className="w-px h-4 bg-gray-300 mx-2" />
      
      <button
        type="button"
        onMouseDown={handleAddLink}
        disabled={isDisabled}
        className={buttonClass(editor.isActive("link"), isDisabled)}
        title="Add/Remove link"
      >
        <LinkIcon className="w-4 h-4 3xl:w-5 3xl:h-5" />
      </button>
    </div>
  );
};

const TextEditor: React.FC<TextEditorProps> = ({
  value = "",
  onChange,
  disabled = false,
  placeholder = "Start typing your feedback here...",
   onFocus, 
  onBlur,
}) => {
  const editor = useEditor({
  extensions: [
    StarterKit.configure({
      heading: false,
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        class: "text-blue-600 underline cursor-pointer hover:text-blue-800",
      },
    }),
  ],
  content: value || "",
  editable: !disabled,
  editorProps: {
    attributes: {
      class:
        "focus:outline-none prose prose-sm max-w-none editor-content-inner",
    },
  },
  onFocus: () => {  // ADD THIS
    onFocus?.();
  },
  onBlur: () => {   // ADD THIS
    onBlur?.();
  },
  onUpdate: ({ editor }) => {
    const html = editor.getHTML();
    if (onChange) {
      onChange(html);
    }

    // Auto-scroll to cursor position when editing
    setTimeout(() => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const editorElement = document.querySelector(".editor-content");

        if (editorElement) {
          const editorRect = editorElement.getBoundingClientRect();
          const scrollTop = editorElement.scrollTop;
          const relativeTop = rect.top - editorRect.top + scrollTop;

          if (rect.bottom > editorRect.bottom - 100) {
            editorElement.scrollTop = relativeTop - editorRect.height + 150;
          }
        }
      }
    }, 10);
  },
});
  useEffect(() => {
    if (!editor) return;

    const currentContent = editor.getHTML();
    if (value !== currentContent && value !== undefined) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);


  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  return (
    <>
      <style>{`
        /* Text Selection Highlight */
        .ProseMirror ::selection {
          background-color: #b4d5fe;
        }
        
        .ProseMirror ::-moz-selection {
          background-color: #b4d5fe;
        }

        /* Editor Focus Styles */
        .ProseMirror-focused {
          outline: none;
        }

        /* Editor Content Scrollbar - Full height container */
        .editor-content {
          overflow-y: auto;
          height: 100%;
        }
        
        /* Add padding to the inner content instead */
        .editor-content-inner {
          min-height: 100%;
          padding-bottom: 80px; /* Space for menu bar */
        }


        /* List Styles */
        .ProseMirror ul,
        .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }

        .ProseMirror ul li {
          list-style-type: disc;
          margin: 0.25rem 0;
        }

        .ProseMirror ol li {
          list-style-type: decimal;
          margin: 0.25rem 0;
        }

        .ProseMirror ul ul,
        .ProseMirror ol ol {
          margin: 0.25rem 0;
        }

        /* Paragraph spacing */
        .ProseMirror p {
          margin: 0.5rem 0;
        }

        .ProseMirror p:first-child {
          margin-top: 0;
        }

        .ProseMirror p:last-child {
          margin-bottom: 0;
        }

        /* Heading styles */
        .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.5rem 0;
          line-height: 1.3;
        }

        .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0.75rem 0 0.5rem 0;
          line-height: 1.4;
        }

        /* Remove default prose styles that might interfere */
        .ProseMirror.prose {
          max-width: none;
        }

        /* Better text formatting display */
        .ProseMirror strong {
          font-weight: 700;
        }

        .ProseMirror em {
          font-style: italic;
        }

        .ProseMirror u {
          text-decoration: underline;
        }

        /* Link Styles */
        .ProseMirror a {
          color: #2563eb;
          text-decoration: underline;
          cursor: pointer;
        }

        .ProseMirror a:hover {
          color: #1e40af;
        }
      `}</style>
      <div
        className={`w-full h-full border rounded-[4px] bg-[#F8F9FC] transition-colors relative ${
          disabled
            ? "border-gray-200"
            : "border-gray-300 focus-within:border-gray-400"
        }`}  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
      >
        {/* Scrollable Editor Content - Full Height */}
        <div className="absolute inset-0 py-3 px-2 editor-content custom-scrollbar-overlay">
          {editor.isEmpty && (
            <div className="absolute top-3 left-2  text-gray-400 font-inter pointer-events-none select-none"  >
              {placeholder}
            </div>
          )}
          <EditorContent editor={editor} className="text-gray-900 h-full font-inter"   />
        </div>

        {/* Menu Bar with Blur Background - Fixed at bottom */}
        <div className="absolute bottom-0 left-0 right-0 pb-2 pointer-events-none">
          <div
            className="backdrop-blur-sm bg-[#F8F9FC]/5 py-2 flex justify-center mr-2"
            style={{
              backdropFilter: "blur(2px)",
              WebkitBackdropFilter: "blur(2px)",
            }}
          >
            <div className="pointer-events-auto">
              <MenuBar editor={editor} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TextEditor;